import { SyntheticEvent, useState } from 'react';

import {
  alpha,
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteGetTagProps,
  AutocompleteRenderInputParams,
  Chip,
  CircularProgress,
  InputLabel,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { mobileUsersApi, multiAccountApi } from '../../axios';
import { useLanguage } from '../../hooks';
import { emailRegex } from '../../utils/validations';

const customStyles = {
  autocomplete: {
    width: '100%',
    '& .MuiAutocomplete-input': {
      minWidth: '20ch', // to avoid overflow of placeholder
    },
  },
  inputRoot: {
    '& .MuiAutocomplete-inputRoot': {
      borderRadius: '8px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: (theme: Theme) => theme.palette.secondary.light,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: (theme: Theme) => theme.palette.primary.main,
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: 1,
        boxShadow: (theme: Theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
      },
      '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
        borderColor: (theme: Theme) => theme.palette.secondary.light,
      },
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          border: (theme: Theme) => `1px solid ${theme.palette.primary.main}`,
          boxShadow: (theme: Theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
          outline: 'none',
        },
      },
    },
  },
  emailChip: {
    backgroundColor: '#3E8FFA33',
    margin: '2px 4px',
    '& .MuiChip-deleteIcon': {
      color: (theme: Theme) => theme.palette.primary.main,
    },
  },
  loadingIndicator: {
    marginRight: '12px',
  },
  label: {
    marginBottom: '6px',
  },
  helpIcon: {
    color: (theme: Theme) => theme.palette.secondary.main,
    marginRight: '12px',
    fontSize: '14px',
    cursor: 'help',
  },
};

/**
 * Whether the email is already taken and therefore cannot be invited.
 *
 * An existing email is not always an error. When it belongs to an identity this
 * account can link to, the invitation is valid and the backend creates the row
 * with an alias over the same base email. The link is only queried once the
 * email is known to exist, so a normal invitation does not pay the extra call.
 */
export async function emailIsTaken(email: string, allowLinkedIdentities: boolean) {
  const { email_exists: emailExists } = await mobileUsersApi.validateNewUserEmail(email);
  if (!emailExists) return false;
  if (!allowLinkedIdentities) return true;

  // Past this point the address is known to be taken, so a failed link query
  // falls back to the usual error rather than to "we could not check". Same rule
  // as `newUserInformation`, which handles the identical state.
  try {
    const { linkable } = await multiAccountApi.checkEmail(email);
    return !linkable;
  } catch (error) {
    console.error(error);
    return true;
  }
}

type MultiEmailInputProps = {
  emails: string[];
  onEmailChange: (emails: string[]) => void;
  validateEmailInUse?: boolean;
  allowLinkedIdentities?: boolean;
  hideLabel?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
};

export const MultiEmailInput = ({
  emails,
  onEmailChange: handleChangeEmails,
  validateEmailInUse = false,
  allowLinkedIdentities = false,
  hideLabel = false,
  disabled = false,
  readOnly = false,
}: MultiEmailInputProps) => {
  const language = useLanguage();
  const { enqueueSnackbar } = useSnackbar();

  const [emailInputValue, setEmailInputValue] = useState('');
  const [validationLoading, setValidationLoading] = useState(false);

  async function checkEmailValid(emailToCheck: string) {
    if (!emailRegex.test(emailToCheck)) {
      enqueueSnackbar(language.mobileUsers.invite_user.emailFormatError, {
        variant: 'error',
      });
      return false;
    }
    if (validateEmailInUse) {
      setValidationLoading(true);
      try {
        const inUse = await checkEmailInUse(emailToCheck);
        return inUse ? false : true;
      } catch (error) {
        // The loading flag gates both Enter and blur, so a rejection escaping
        // here would leave the input permanently unusable. Only the first round
        // trip can reach this: answering it with "already in use" would state
        // something about an address that is very likely free.
        console.error(error);
        enqueueSnackbar(language.wizard.emailCheckUnavailable, { variant: 'error' });
        return false;
      } finally {
        setValidationLoading(false);
      }
    }
    return true;
  }

  const checkEmailInUse = async (emailToCheck: string) => {
    const taken = await emailIsTaken(emailToCheck, allowLinkedIdentities);
    if (taken) enqueueSnackbar(language.wizard.emailExistsError, { variant: 'error' });
    return taken;
  };

  async function handleEnterEmail() {
    const emailValid = await checkEmailValid(emailInputValue);
    if (!emailValid) return;
    handleChangeEmails([...emails, emailInputValue]);
    setEmailInputValue('');
  }

  function handleOnChangeList(
    _event: SyntheticEvent<Element, Event>,
    value: string[],
    reason: AutocompleteChangeReason,
  ) {
    switch (reason) {
      case 'removeOption':
        handleChangeEmails(value);
        break;
      case 'clear':
        handleChangeEmails([]);
        break;
    }
  }

  async function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (validationLoading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if ([' ', ',', 'Enter'].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      if (emailInputValue === '' || event.repeat) return;
      await handleEnterEmail();
    }
  }

  async function handleInputBlur(event: React.FocusEvent<HTMLInputElement>) {
    if (validationLoading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (emailInputValue === '') return;
    await handleEnterEmail();
  }

  const renderTags = (value: string[], getTagProps: AutocompleteGetTagProps) => {
    return (
      <>
        {value.map((option, index) => (
          <Chip
            key={`mail-chip-${index}`}
            label={option}
            size='small'
            {...getTagProps({ index })}
            sx={customStyles.emailChip}
          />
        ))}
        {validationLoading && (
          <Chip
            key='mail-chip-new'
            label={emailInputValue}
            size='small'
            sx={customStyles.emailChip}
            onDelete={() => {
              return;
            }} // needed to show delete icon
            deleteIcon={<CircularProgress size={13} />}
          />
        )}
      </>
    );
  };

  const renderInput = (params: AutocompleteRenderInputParams) => {
    return (
      <>
        {!hideLabel && (
          <InputLabel htmlFor='multi-email-input'>
            <Typography
              variant='body1'
              color='textPrimary'
              sx={customStyles.label}
            >
              {language.mobileUsers.invite_user.emails}
            </Typography>
          </InputLabel>
        )}
        <TextField
          {...params}
          id='multi-email-input'
          variant='outlined'
          placeholder={language.mobileUsers.invite_user.placeholder}
          InputProps={{
            ...params.InputProps,
            value: emailInputValue,
            onKeyDown: handleInputKeyDown,
            onChange: (e) => setEmailInputValue(e.target.value),
            onBlur: handleInputBlur,
            endAdornment: params.InputProps.endAdornment,
          }}
          inputProps={{
            ...params.inputProps,
            value: emailInputValue,
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
          }}
        />
      </>
    );
  };

  return (
    <Autocomplete
      multiple
      disabled={disabled || readOnly}
      value={emails}
      onChange={handleOnChangeList}
      options={[]}
      size='small'
      sx={{
        ...customStyles.autocomplete,
        ...customStyles.inputRoot,
        ...(readOnly && {
          '& .MuiAutocomplete-inputRoot.Mui-disabled': {
            backgroundColor: '#FAFBFF',
            opacity: 1,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e5e7eb',
            },
          },
          '& .MuiChip-root.Mui-disabled': {
            opacity: 1,
            userSelect: 'text',
            WebkitUserSelect: 'text',
            '& .MuiChip-deleteIcon': {
              display: 'none',
            },
          },
          '& .MuiAutocomplete-endAdornment': {
            display: 'none',
          },
          '& .MuiAutocomplete-input.Mui-disabled': {
            display: 'none',
          },
        }),
      }}
      renderTags={renderTags}
      renderInput={renderInput}
      freeSolo
    />
  );
};
